// frontend/admin-portal/src/components/products/ProductLabelsModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Input,
  SearchSelect,
  formatMoney,
} from "@fiberhome/shared-ui";
import { useSettings } from "../../contexts/SettingsContext";
import { resolveImageUrl } from "../../services/fileService";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

/* ============================================================
 *  Label sizes
 * ============================================================ */
const LABEL_SIZES = [
  {
    id: "avery-l7160",
    group: "Avery A4",
    name: "Avery L7160 — 21 per page",
    pageW: 210,
    pageH: 297,
    cols: 3,
    rows: 7,
    labelW: 63.5,
    labelH: 38.1,
    marginX: 7,
    marginY: 12.5,
    gapX: 2.5,
    gapY: 0,
  },
  {
    id: "avery-l7163",
    group: "Avery A4",
    name: "Avery L7163 — 14 per page",
    pageW: 210,
    pageH: 297,
    cols: 2,
    rows: 7,
    labelW: 99.1,
    labelH: 38.1,
    marginX: 4.65,
    marginY: 12.5,
    gapX: 2.5,
    gapY: 0,
  },
  {
    id: "avery-l7651",
    group: "Avery A4",
    name: "Avery L7651 — 65 per page (mini)",
    pageW: 210,
    pageH: 297,
    cols: 5,
    rows: 13,
    labelW: 38.1,
    labelH: 21.2,
    marginX: 10.7,
    marginY: 8.7,
    gapX: 2.5,
    gapY: 0,
  },
  {
    id: "avery-l7159",
    group: "Avery A4",
    name: "Avery L7159 — 24 per page",
    pageW: 210,
    pageH: 297,
    cols: 3,
    rows: 8,
    labelW: 64,
    labelH: 34,
    marginX: 7,
    marginY: 13,
    gapX: 2.5,
    gapY: 0,
  },
  {
    id: "shelf-small",
    group: "Shelf labels",
    name: "Shelf label — small (48 × 30 mm)",
    pageW: 210,
    pageH: 297,
    cols: 4,
    rows: 9,
    labelW: 48,
    labelH: 30,
    marginX: 6,
    marginY: 6,
    gapX: 2,
    gapY: 2,
  },
  {
    id: "shelf-wide",
    group: "Shelf labels",
    name: "Shelf label — wide (70 × 30 mm)",
    pageW: 210,
    pageH: 297,
    cols: 3,
    rows: 9,
    labelW: 62,
    labelH: 30,
    marginX: 8,
    marginY: 6,
    gapX: 3,
    gapY: 2,
  },
  {
    id: "price-tag",
    group: "Price tags",
    name: "Price tag (30 × 20 mm)",
    pageW: 210,
    pageH: 297,
    cols: 6,
    rows: 13,
    labelW: 30,
    labelH: 20,
    marginX: 6,
    marginY: 5,
    gapX: 1.5,
    gapY: 1.5,
  },
  {
    id: "single-a4",
    group: "Full page",
    name: "Single label per A4",
    pageW: 210,
    pageH: 297,
    cols: 1,
    rows: 1,
    labelW: 190,
    labelH: 277,
    marginX: 10,
    marginY: 10,
    gapX: 0,
    gapY: 0,
  },
];

const GROUPED_SIZES = LABEL_SIZES.reduce((acc, s) => {
  if (!acc[s.group]) acc[s.group] = [];
  acc[s.group].push(s);
  return acc;
}, {});

const sizeOptions = Object.entries(GROUPED_SIZES).map(([group, sizes]) => ({
  group,
  options: sizes.map((s) => ({ value: s.id, label: s.name })),
}));

/* ============================================================
 *  Templates
 * ============================================================ */
const TEMPLATES = [
  {
    id: "shelf",
    name: "Shelf",
    fields: ["name", "sku", "price", "barcode"],
    barcodeType: "CODE128",
    barcodeHeight: 20,
    showBarcodeText: true,
    showLogo: true,
    showStoreName: false,
  },
  {
    id: "detail",
    name: "Detail",
    fields: ["name", "sku", "category", "price", "barcode"],
    barcodeType: "CODE128",
    barcodeHeight: 24,
    showBarcodeText: true,
    showLogo: true,
    showStoreName: true,
  },
  {
    id: "price",
    name: "Price Tag",
    fields: ["name", "price"],
    barcodeType: "none",
    barcodeHeight: 0,
    showBarcodeText: false,
    showLogo: false,
    showStoreName: false,
  },
  {
    id: "compact",
    name: "Compact",
    fields: ["sku", "price", "barcode"],
    barcodeType: "CODE128",
    barcodeHeight: 16,
    showBarcodeText: false,
    showLogo: false,
    showStoreName: false,
  },
  {
    id: "qr",
    name: "QR code",
    fields: ["name", "price", "qrcode"],
    barcodeType: "QR",
    barcodeHeight: 30,
    showBarcodeText: false,
    showLogo: true,
    showStoreName: true,
  },
];

const ALL_FIELDS = [
  { key: "name", label: "Product Name" },
  { key: "sku", label: "SKU" },
  { key: "barcode", label: "Barcode" },
  { key: "qrcode", label: "QR Code" },
  { key: "price", label: "Selling Price" },
  { key: "cost", label: "Cost (FOB)" },
  { key: "totalCost", label: "Total Cost (Landed)" },
  { key: "margin", label: "Margin %" },
  { key: "category", label: "Category" },
  { key: "brand", label: "Brand" },
  { key: "unit", label: "Unit" },
  { key: "stock", label: "Stock" },
  { key: "shortDescription", label: "Short Description" },
];

/* ============================================================
 *  Barcode + QR canvases
 * ============================================================ */
function BarcodeCanvas({ value, type, height, fontSize, showText = true }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, String(value), {
        format: type === "EAN13" ? "EAN13" : "CODE128",
        width: 1.2,
        height,
        displayValue: showText,
        fontSize: fontSize || 9,
        margin: 0,
        lineColor: "#000",
        background: "#fff",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Barcode render failed:", err.message);
    }
  }, [value, type, height, fontSize, showText]);

  if (!value) return null;
  return (
    <canvas
      ref={ref}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    />
  );
}

function QrCanvas({ value, size }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    QRCode.toCanvas(ref.current, String(value), {
      width: size || 64,
      margin: 0,
      color: { dark: "#000", light: "#fff" },
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("QR render failed:", err.message);
    });
  }, [value, size]);

  if (!value) return null;
  return (
    <canvas ref={ref} style={{ display: "block", width: size, height: size }} />
  );
}

/* ============================================================
 *  LabelPreview — one component, two rendering modes
 *
 *  mode = "fixed"   → mm sizing for A4 sheet + print
 *  mode = "preview" → % of parent, fonts in em, logo in px
 *                     (previewHeightPx makes the logo scale with the
 *                     actual rendered label height)
 * ============================================================ */
function LabelPreview({
  product,
  size,
  state,
  store,
  currency,
  scale = 1,
  mode = "fixed",
  previewHeightPx = 300, // ← NEW: prop, with a safe default
}) {
  const isPreview = mode === "preview";
  const fields = new Set(state.fields);

  const showLogo =
    state.showLogo && store.logo && (size.labelW > 30 || size.labelH > 20);
  const showStoreName =
    state.showStoreName && (size.labelW > 30 || size.labelH > 20);

  const cur = product.currency || currency;
  const price = Number(product.price) || 0;
  const cost = Number(product.costPrice) || 0;
  const totalCost = Number(product.totalCost) || 0;
  const margin = price > 0 ? ((price - (totalCost || cost)) / price) * 100 : 0;

  const barcodeValue = product.barcode || product.sku || "";
  const qrPayload = product.sku || barcodeValue;
  const hasBarcode = fields.has("barcode") || fields.has("qrcode");

  const barcodeCols = hasBarcode
    ? size.labelW > size.labelH
      ? "1fr 40%"
      : "1fr 60%"
    : "1fr";

  const baseMm = Math.min(size.labelW / 8, size.labelH / 6);
  const nameFs = isPreview ? "1.7em" : `${Math.max(6, baseMm)}px`;
  const smallFs = isPreview ? "1.15em" : `${Math.max(5, baseMm * 0.7)}px`;
  const priceFs = isPreview ? "2.2em" : `${Math.max(8, baseMm * 1.2)}px`;
  const gapMm = isPreview ? "0.25em" : `${Math.max(1, size.labelH * 0.04)}mm`;
  const padMm = isPreview ? "0.5em" : `${Math.max(2, size.labelH * 0.06)}mm`;

  // Logo sizing
  // Preview mode: proportional to rendered height, clamped 20–60px.
  // Fixed mode: 12% of label height in mm (print-safe).
  const logoStyle = (() => {
    if (isPreview) {
      const px = Math.max(20, Math.min(60, previewHeightPx * 0.18));
      return {
        height: `${px}px`,
        maxWidth: "40%",
        objectFit: "contain",
        flexShrink: 0,
      };
    }
    return {
      height: `${Math.max(3, size.labelH * 0.12)}mm`,
      maxWidth: `${size.labelW * 0.3}mm`,
      objectFit: "contain",
    };
  })();

  const outerStyle = isPreview
    ? {
        width: "100%",
        height: "100%",
        padding: padMm,
        background: "#fff",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: barcodeCols,
        gap: gapMm,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        overflow: "hidden",
      }
    : {
        width: `${size.labelW * scale}mm`,
        height: `${size.labelH * scale}mm`,
        padding: padMm,
        background: "#fff",
        border: "0.5px solid #d1d5db",
        borderRadius: 1,
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: barcodeCols,
        gap: gapMm,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        overflow: "hidden",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      };

  return (
    <div className="fh-label" style={outerStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minWidth: 0,
          minHeight: 0,
          overflow: "hidden",
          gap: isPreview ? "0.15em" : 0,
        }}
      >
        {(showLogo || showStoreName) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isPreview
                ? `${Math.max(6, Math.min(14, previewHeightPx * 0.02))}px`
                : gapMm,
              marginBottom: gapMm,
              minWidth: 0,
            }}
          >
            {showLogo && (
              <img
                src={resolveImageUrl(store.logo)}
                alt=""
                style={logoStyle}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            {showStoreName && (
              <div
                style={{
                  fontSize: isPreview
                    ? `${Math.max(11, Math.min(20, previewHeightPx * 0.05))}px`
                    : smallFs,
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                }}
              >
                {store.name || "FiberHome"}
              </div>
            )}
          </div>
        )}

        {fields.has("name") && (
          <div
            style={{
              fontSize: nameFs,
              fontWeight: 700,
              lineHeight: 1.15,
              color: "#0f172a",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: isPreview ? 2 : 3,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {product.name}
          </div>
        )}

        {(fields.has("sku") ||
          fields.has("category") ||
          fields.has("brand")) && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: gapMm,
              fontSize: smallFs,
              color: "#6b7280",
              fontFamily: "ui-monospace, monospace",
              lineHeight: 1.1,
              marginTop: gapMm,
            }}
          >
            {fields.has("sku") && product.sku && <span>{product.sku}</span>}
            {fields.has("category") && product.category?.name && (
              <span>· {product.category.name}</span>
            )}
            {fields.has("brand") && product.brand?.name && (
              <span>· {product.brand.name}</span>
            )}
          </div>
        )}

        {(fields.has("price") ||
          fields.has("cost") ||
          fields.has("totalCost") ||
          fields.has("margin")) && (
          <div
            style={{
              display: "flex",
              gap: gapMm,
              alignItems: "baseline",
              flexWrap: "wrap",
              marginTop: gapMm,
            }}
          >
            {fields.has("price") && (
              <div
                style={{
                  fontSize: priceFs,
                  fontWeight: 800,
                  color: "#0f172a",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.1,
                }}
              >
                {formatMoney(price, { symbol: cur })}
              </div>
            )}
            {fields.has("cost") && (
              <div
                style={{ fontSize: smallFs, color: "#6b7280", lineHeight: 1.1 }}
              >
                cost {formatMoney(cost, { symbol: cur })}
              </div>
            )}
            {fields.has("totalCost") && (
              <div
                style={{ fontSize: smallFs, color: "#6b7280", lineHeight: 1.1 }}
              >
                landed {formatMoney(totalCost, { symbol: cur })}
              </div>
            )}
            {fields.has("margin") && (
              <div
                style={{
                  fontSize: smallFs,
                  fontWeight: 600,
                  color:
                    margin >= 30
                      ? "#198754"
                      : margin >= 10
                        ? "#f59e0b"
                        : "#dc3545",
                  lineHeight: 1.1,
                }}
              >
                {margin.toFixed(0)}%
              </div>
            )}
          </div>
        )}

        {(fields.has("unit") || fields.has("stock")) && (
          <div style={{ fontSize: smallFs, color: "#6b7280", lineHeight: 1.1 }}>
            {fields.has("stock") && (
              <span>
                {product.stock} {product.unit || "pcs"}
              </span>
            )}
            {fields.has("unit") && !fields.has("stock") && (
              <span>{product.unit || "pcs"}</span>
            )}
          </div>
        )}

        {fields.has("shortDescription") && product.shortDescription && (
          <div
            style={{
              fontSize: smallFs,
              color: "#6b7280",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: isPreview ? 1 : 2,
              WebkitBoxOrient: "vertical",
              lineHeight: 1.1,
            }}
          >
            {product.shortDescription}
          </div>
        )}
      </div>

      {hasBarcode && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          {fields.has("barcode") &&
            state.barcodeType !== "none" &&
            state.barcodeType !== "QR" &&
            barcodeValue && (
              <BarcodeCanvas
                value={barcodeValue}
                type={state.barcodeType}
                height={
                  isPreview
                    ? 60
                    : Math.max(
                        12,
                        Math.min(
                          state.barcodeHeight * scale,
                          size.labelH * 2.5,
                        ),
                      )
                }
                fontSize={isPreview ? 12 : Math.max(7, baseMm * 0.6)}
                showText={state.showBarcodeText}
              />
            )}
          {fields.has("qrcode") && qrPayload && (
            <QrCanvas
              value={qrPayload}
              size={
                isPreview
                  ? 90
                  : Math.max(
                      24,
                      Math.min(size.labelH * 2.6 * scale, 80 * scale),
                    )
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 *  DesignerPreview — measures its own box and drives LabelPreview
 * ============================================================ */
function DesignerPreview({ product, size, state, store, currency }) {
  const wrapRef = useRef(null);
  const [metrics, setMetrics] = useState({ font: 12, heightPx: 300 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const smaller = Math.min(rect.width, rect.height);
      let nextFont = smaller * 0.035;
      nextFont = Math.min(nextFont, 22);
      nextFont = Math.max(8, Math.round(nextFont));

      setMetrics({ font: nextFont, heightPx: rect.height });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [size.id]);

  if (!product) {
    return <div className="text-muted">Select products first.</div>;
  }

  const aspect = `${size.labelW} / ${size.labelH}`;

  return (
    <div
      ref={wrapRef}
      className="fh-label-designer__canvas"
      style={{ aspectRatio: aspect, fontSize: `${metrics.font}px` }}
    >
      <LabelPreview
        product={product}
        size={size}
        state={state}
        store={store}
        currency={currency}
        mode="preview"
        previewHeightPx={metrics.heightPx} // ← FIX: pass the actual number
      />
    </div>
  );
}

/* ============================================================
 *  A4Sheet
 * ============================================================ */
function A4Sheet({ products, size, state, store, currency }) {
  const PREVIEW_W = 500;
  const scale = PREVIEW_W / size.pageW;
  const perSheet = size.cols * size.rows;

  const pages = [];
  for (let i = 0; i < products.length; i += perSheet) {
    pages.push(products.slice(i, i + perSheet));
  }

  if (pages.length === 0) {
    return (
      <div
        className="text-muted"
        style={{ padding: "2rem", textAlign: "center" }}
      >
        Select products first.
      </div>
    );
  }

  return (
    <div className="fh-a4-sheet">
      {pages.map((pageItems, pageIdx) => (
        <div
          key={pageIdx}
          className="fh-a4-sheet__page"
          style={{
            width: `${size.pageW * scale}px`,
            height: `${size.pageH * scale}px`,
            padding: `${size.marginY * scale}px ${size.marginX * scale}px`,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${size.cols}, 1fr)`,
              gap: `${size.gapY * scale}px ${size.gapX * scale}px`,
              width: "100%",
              height: "100%",
            }}
          >
            {Array.from({ length: perSheet }).map((_, i) => {
              const p = pageItems[i];
              if (!p) {
                return (
                  <div
                    key={`empty-${i}`}
                    style={{
                      border: "0.5px dashed #e5e7eb",
                      borderRadius: 2,
                      background: "#fafafa",
                    }}
                  />
                );
              }
              return (
                <LabelPreview
                  key={p.id}
                  product={p}
                  size={size}
                  state={state}
                  store={store}
                  currency={currency}
                  scale={scale}
                  mode="fixed"
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
 *  Main modal
 * ============================================================ */
export default function ProductLabelsModal({
  open,
  products = [],
  currency = "USD",
  onClose,
}) {
  const { get } = useSettings();

  const store = useMemo(
    () => ({
      name: get("store.name", "FiberHome"),
      logo: get("store.logo", null),
      address: get("store.address", ""),
      city: get("store.city", ""),
      phone: get("store.phone", ""),
      email: get("store.email", ""),
      website: get("store.website", ""),
      taxId: get("store.taxId", ""),
    }),
    [get],
  );

  const [tab, setTab] = useState("designer");
  const [sizeId, setSizeId] = useState("shelf-small");
  const [templateId, setTemplateId] = useState("shelf");
  const [fields, setFields] = useState(["name", "sku", "price", "barcode"]);
  const [barcodeType, setBarcodeType] = useState("CODE128");
  const [barcodeHeight, setBarcodeHeight] = useState(20);
  const [showBarcodeText, setShowBarcodeText] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [showStoreName, setShowStoreName] = useState(false);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    setFields(tpl.fields);
    setBarcodeType(tpl.barcodeType);
    setBarcodeHeight(tpl.barcodeHeight);
    setShowBarcodeText(tpl.showBarcodeText);
    setShowLogo(tpl.showLogo);
    setShowStoreName(tpl.showStoreName);
  };

  useEffect(() => {
    if (!open) return;
    applyTemplate("shelf");
    setTab("designer");
  }, [open]);

  const size = useMemo(
    () => LABEL_SIZES.find((s) => s.id === sizeId) || LABEL_SIZES[0],
    [sizeId],
  );

  const state = {
    fields,
    barcodeType,
    barcodeHeight,
    showBarcodeText,
    showLogo,
    showStoreName,
  };

  const handlePrint = () => {
    document.body.classList.add("fh-print-labels");
    setTimeout(() => {
      try {
        window.print();
      } finally {
        setTimeout(
          () => document.body.classList.remove("fh-print-labels"),
          500,
        );
      }
    }, 150);
  };

  const toggleField = (key) => {
    setFields((cur) =>
      cur.includes(key) ? cur.filter((f) => f !== key) : [...cur, key],
    );
  };

  const firstProduct = products[0];
  const pageCount = Math.ceil(products.length / (size.cols * size.rows)) || 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Print Labels — ${products.length} product${products.length === 1 ? "" : "s"}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            🖨 Print {products.length} label{products.length === 1 ? "" : "s"}
          </Button>
        </>
      }
    >
      <div className="fh-label-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "designer"}
          className={`fh-label-tab ${tab === "designer" ? "is-active" : ""}`}
          onClick={() => setTab("designer")}
        >
          Label
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "sheet"}
          className={`fh-label-tab ${tab === "sheet" ? "is-active" : ""}`}
          onClick={() => setTab("sheet")}
        >
          A4 Sheet
          <span className="fh-label-tab__badge">{pageCount}</span>
        </button>
      </div>

      {tab === "designer" && (
        <div className="fh-label-designer no-print">
          <div className="fh-label-designer__controls">
            <div className="fh-label-designer__section">
              <div className="fh-label-designer__title">Template</div>
              <div className="fh-label-template-chips">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`fh-label-template-chip ${templateId === t.id ? "is-active" : ""}`}
                    onClick={() => applyTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="fh-label-designer__section">
              <div className="fh-label-designer__title">Sticker size</div>
              <SearchSelect
                value={sizeId}
                onChange={(v) => v && setSizeId(v)}
                options={sizeOptions}
                placeholder="Choose a sticker size…"
                searchPlaceholder="Search sizes…"
                emptyMessage="No sizes match"
              />
              <div
                className="text-muted"
                style={{ fontSize: 11, marginTop: 6 }}
              >
                {size.labelW} × {size.labelH} mm · {size.cols}×{size.rows} ={" "}
                {size.cols * size.rows} per A4
              </div>
            </div>

            <div className="fh-label-designer__section">
              <div className="fh-label-designer__title">
                Fields to print ({fields.length})
              </div>
              <div className="fh-label-fields">
                {ALL_FIELDS.map((f) => (
                  <label key={f.key} className="fh-checkbox">
                    <input
                      type="checkbox"
                      checked={fields.includes(f.key)}
                      onChange={() => toggleField(f.key)}
                    />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {(fields.includes("barcode") || fields.includes("qrcode")) && (
              <div className="fh-label-designer__section">
                <div className="fh-label-designer__title">Barcode / QR</div>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                  {[
                    { id: "CODE128", name: "Code 128" },
                    { id: "EAN13", name: "EAN-13" },
                    { id: "QR", name: "QR code" },
                    { id: "none", name: "None" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`fh-label-template-chip ${barcodeType === t.id ? "is-active" : ""}`}
                      onClick={() => setBarcodeType(t.id)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
                {barcodeType !== "none" && barcodeType !== "QR" && (
                  <>
                    <Input
                      label="Barcode height (px)"
                      type="number"
                      min="10"
                      max="80"
                      value={barcodeHeight}
                      onChange={(e) =>
                        setBarcodeHeight(Number(e.target.value) || 20)
                      }
                    />
                    <label className="fh-checkbox mt-2">
                      <input
                        type="checkbox"
                        checked={showBarcodeText}
                        onChange={(e) => setShowBarcodeText(e.target.checked)}
                      />
                      <span>Show barcode value under bars</span>
                    </label>
                  </>
                )}
              </div>
            )}

            <div className="fh-label-designer__section">
              <div className="fh-label-designer__title">Branding</div>
              <label className="fh-checkbox">
                <input
                  type="checkbox"
                  checked={showLogo}
                  onChange={(e) => setShowLogo(e.target.checked)}
                />
                <span>Show store logo</span>
              </label>
              <label className="fh-checkbox mt-1">
                <input
                  type="checkbox"
                  checked={showStoreName}
                  onChange={(e) => setShowStoreName(e.target.checked)}
                />
                <span>Show store name</span>
              </label>
              {!store.logo && (
                <div
                  className="text-muted"
                  style={{ fontSize: 11, marginTop: 4 }}
                >
                  No logo in Settings → Store Identity.
                </div>
              )}
            </div>
          </div>

          <div className="fh-label-designer__preview">
            <div className="fh-label-designer__preview-inner">
              <DesignerPreview
                product={firstProduct}
                size={size}
                state={state}
                store={store}
                currency={currency}
              />
            </div>
            {products.length > 1 && (
              <div className="fh-label-designer__preview-note">
                Showing 1st of {products.length}. Open <strong>A4 Sheet</strong>{" "}
                to see all labels laid out per page.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "sheet" && (
        <div className="fh-label-sheet-tab no-print">
          <A4Sheet
            products={products}
            size={size}
            state={state}
            store={store}
            currency={currency}
          />
        </div>
      )}

      <div
        className="fh-label-sheet fh-label-sheet--print"
        style={{
          "--label-cols": size.cols,
          "--label-gap-x": `${size.gapX}mm`,
          "--label-gap-y": `${size.gapY}mm`,
          "--label-margin-x": `${size.marginX}mm`,
          "--label-margin-y": `${size.marginY}mm`,
        }}
      >
        {products.map((p) => (
          <LabelPreview
            key={p.id}
            product={p}
            size={size}
            state={state}
            store={store}
            currency={currency}
            scale={1}
            mode="fixed"
          />
        ))}
      </div>
    </Modal>
  );
}
